import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
    Alert, Badge, Box, Button, Center, Group, Loader, Menu, ActionIcon, Modal, MultiSelect,
    NumberInput, Select, SimpleGrid, Stack, Tabs, Text, TextInput, Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    AlertTriangle, Building2, HeartPulse, KeyRound, Mail, MoreVertical, Plus, Settings2, Users as UsersIcon,
} from 'lucide-react';
import { FEATURE_CATALOG, METERING_CATEGORY_META, Roles, RoleDisplayNames } from '@bmai/shared';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import DrillBreadcrumb from '@/design/DrillBreadcrumb';
import StatBand, { type StatItem } from '@/design/StatBand';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { OperatorTenantDetailViewModel } from './OperatorTenantDetailViewModel';
import type { TenantUser } from './OperatorTenantUsersService';

const STATUS_COLOR: Record<string, string> = { active: 'green', trial: 'blue', suspended: 'gray', cancelled: 'red' };
const HEALTH_COLOR: Record<string, string> = { ok: 'green', warn: 'yellow', critical: 'red' };
const HEALTH_TOKEN: Record<string, string> = {
    ok: 'var(--bm-color-success)',
    warn: 'var(--bm-color-caution)',
    critical: 'var(--bm-color-danger)',
};
const ENTITLEMENT_STATUS_OPTIONS = ['active', 'paused', 'expired', 'cancelled'];
const ROLE_LABELS = RoleDisplayNames as Record<string, string>;
function roleLabel(r: string): string { return ROLE_LABELS[r] ?? r; }
const TENANT_ROLE_OPTIONS = [Roles.ADMIN, Roles.VIEWER, Roles.FINANCE, Roles.SECURITY]
    .map((r) => ({ value: r, label: roleLabel(r) }));

async function act(p: Promise<void>, successMsg: string): Promise<void> {
    try {
        await p;
        notifications.show({ color: 'green', message: successMsg });
    } catch (e) {
        notifications.show({ color: 'red', title: 'Action failed', message: e instanceof Error ? e.message : 'Unexpected error' });
    }
}

async function resendEmail(p: Promise<{ emailSent: boolean }>, kind: 'invite' | 'reset'): Promise<void> {
    const label = kind === 'reset' ? 'Password reset email' : 'Invite';
    try {
        const r = await p;
        notifications.show({ color: r.emailSent ? 'green' : 'orange', message: r.emailSent ? `${label} sent` : `${label} could not be delivered` });
    } catch (e) {
        notifications.show({ color: 'red', title: 'Send failed', message: e instanceof Error ? e.message : 'Unexpected error' });
    }
}

export default function OperatorTenantDetailPage(): React.ReactElement {
    const { tenantId = '' } = useParams();
    const vm = useViewModel(OperatorTenantDetailViewModel);

    useEffect(() => { void vm.load(tenantId); }, [vm, tenantId]);

    if (vm.loading && !vm.tenant) {
        return <Center h="60vh"><Loader /></Center>;
    }

    const t = vm.tenant;

    return (
        <Stack gap="md">
            <DrillBreadcrumb
                icon={Building2}
                mb={0}
                items={[{ label: 'Tenants', to: '/operator/tenants' }, { label: t?.name ?? tenantId }]}
            />

            <PageHeaderComponent
                title={t?.name ?? tenantId}
                subtitle={t?.id ?? tenantId}
                rightToolbar={
                    <Group gap="xs">
                        {t && <Badge color={STATUS_COLOR[t.status] ?? 'gray'} variant="light">{t.status}</Badge>}
                        {vm.health && <Badge color={HEALTH_COLOR[vm.health.health] ?? 'gray'} variant="filled">{vm.health.health}</Badge>}
                    </Group>
                }
            />

            {vm.error && <Alert color="red" icon={<AlertTriangle size={16} />}>{vm.error}</Alert>}

            <Tabs defaultValue="overview" keepMounted={false}>
                <Tabs.List>
                    <Tabs.Tab value="overview" leftSection={<HeartPulse size={15} />}>Overview</Tabs.Tab>
                    <Tabs.Tab value="users" leftSection={<UsersIcon size={15} />}>Users</Tabs.Tab>
                    <Tabs.Tab value="management" leftSection={<Settings2 size={15} />}>Management</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview" pt="xl"><OverviewTab vm={vm} /></Tabs.Panel>
                <Tabs.Panel value="users" pt="xl"><UsersTab vm={vm} /></Tabs.Panel>
                <Tabs.Panel value="management" pt="xl"><ManagementTab vm={vm} /></Tabs.Panel>
            </Tabs>
        </Stack>
    );
}

const CARD_STYLE = {
    border: '1px solid var(--bm-color-border)',
    borderRadius: 'var(--bm-radius)',
    background: 'var(--bm-color-card)',
    boxShadow: 'var(--bm-card-shadow)',
} as const;

/** A titled content card — used for the Overview cards and Management sections. */
function ManageCard({ title, description, children }: { title: string; description?: string; children: ReactNode }): React.ReactElement {
    return (
        <Box style={{ ...CARD_STYLE, padding: 'var(--mantine-spacing-lg)' }}>
            <Text size="sm" fw={600}>{title}</Text>
            {description !== undefined && <Text size="xs" c="dimmed" mb="md">{description}</Text>}
            {children}
        </Box>
    );
}

/** Theme-token fill for a usage bar. */
function meterFill(pct: number, overage: boolean, unlimited: boolean): string {
    if (unlimited) return 'var(--bm-color-accent-neutral-soft)';
    if (overage) return 'var(--bm-color-danger)';
    if (pct >= 90) return 'var(--bm-color-caution)';
    return 'var(--bm-color-brand-accent)';
}

/** A flat usage bar in the app's ranked-bar idiom: label, mono value, accent-fill track. */
function MeterBar({ label, used, limit, overage = false }: { label: string; used: number; limit: number | null; overage?: boolean }): React.ReactElement {
    const unlimited = limit === null;
    const pct = unlimited ? 100 : limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
    return (
        <Box>
            <Group justify="space-between" mb={6} wrap="nowrap">
                <Text size="sm" fw={600}>{label}</Text>
                <Text size="sm" ff="monospace" fw={700} style={overage ? { color: 'var(--bm-color-danger-text)' } : undefined}>
                    {used.toLocaleString()} <Text span c="dimmed" ff="monospace" fw={400}>/ {unlimited ? '∞' : limit.toLocaleString()}</Text>
                </Text>
            </Group>
            <Box style={{ height: 8, background: 'var(--bm-color-surface)', borderRadius: 4, overflow: 'hidden' }}>
                <Box style={{ height: '100%', width: `${pct}%`, background: meterFill(pct, overage, unlimited), borderRadius: 4 }} />
            </Box>
        </Box>
    );
}

function OverviewTab({ vm }: { vm: OperatorTenantDetailViewModel }): React.ReactElement {
    const h = vm.health;
    const ent = vm.entitlement;
    const created = vm.tenant?.createdAt;
    const stats: StatItem[] = [
        { label: 'Plan', value: ent?.planName ?? h?.planName ?? '—' },
        { label: 'Health', value: h ? h.health.toUpperCase() : '—', ...(h ? { valueColor: HEALTH_TOKEN[h.health] } : {}) },
        { label: 'Active Users', value: String(h?.activeUsers ?? vm.users?.length ?? '—') },
        { label: 'Created', value: created ? new Date(created).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—' },
    ];
    return (
        <Stack gap="lg">
            <StatBand stats={stats} />

            {h && h.flags.length > 0 && (
                <Alert color={h.health === 'critical' ? 'red' : 'yellow'} icon={<AlertTriangle size={16} />} title="Flags" variant="light">
                    <Group gap="xs">{h.flags.map((f) => <Badge key={f} variant="light" color={h.health === 'critical' ? 'red' : 'yellow'}>{f}</Badge>)}</Group>
                </Alert>
            )}

            <UsageSection vm={vm} />

            {ent && (
                <ManageCard title="Entitlement" description="What this tenant is currently entitled to.">
                    <Group gap="xs" mb="sm">
                        <Text size="sm" c="dimmed">Status</Text>
                        <Badge variant="light" size="sm">{ent.status}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed" mb={6}>Features</Text>
                    <Group gap={6}>
                        {ent.enabledFeatures.length === 0 ? <Text size="sm" c="dimmed">None</Text> :
                            ent.enabledFeatures.map((f) => <Badge key={f} variant="outline" size="sm">{FEATURE_CATALOG.find((c) => c.key === f)?.label ?? f}</Badge>)}
                    </Group>
                </ManageCard>
            )}
        </Stack>
    );
}

function UsageSection({ vm }: { vm: OperatorTenantDetailViewModel }): React.ReactElement | null {
    const buckets = vm.quotas;
    if (buckets === null || buckets.length === 0) return null;
    return (
        <ManageCard title="Usage this period" description="Consumption against each quota bucket for the current billing month.">
            <Stack gap="lg">
                {buckets.map((b) => (
                    <MeterBar key={b.category} label={METERING_CATEGORY_META[b.category].label} used={b.used} limit={b.limit} overage={b.overage} />
                ))}
            </Stack>
        </ManageCard>
    );
}

function UsersTab({ vm }: { vm: OperatorTenantDetailViewModel }): React.ReactElement {
    const [inviteOpen, setInviteOpen] = useState(false);
    const columns: MegaGridColumn<TenantUser>[] = [
        {
            key: 'email', header: 'User', sortValue: (u) => u.email,
            render: (u) => (
                <div>
                    <Text size="sm" fw={500}>{u.displayName || u.email}</Text>
                    <Text size="xs" c="dimmed">{u.email}</Text>
                </div>
            ),
        },
        { key: 'roles', header: 'Roles', sortValue: (u) => u.roles.join(','), render: (u) => (
            <Group gap={4}>{u.roles.length === 0 ? <Text size="xs" c="dimmed">—</Text> : u.roles.map((r) => <Badge key={r} size="xs" variant="light">{roleLabel(r)}</Badge>)}</Group>
        ) },
        {
            key: 'status', header: 'Status', sortValue: (u) => u.status,
            render: (u) => <Badge size="sm" variant="light" color={u.status === 'active' ? 'green' : 'gray'}>{u.status}</Badge>,
        },
        {
            key: 'actions', header: '', width: 56, align: 'right', noFilter: true,
            render: (u) => {
                const enabled = u.status === 'active';
                return (
                    <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" loading={vm.isUserBusy(u.id)} aria-label="Actions"><MoreVertical size={16} /></ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item leftSection={<Mail size={14} />} onClick={() => void resendEmail(vm.resendInvite(u.id, 'invite'), 'invite')}>
                                Resend invite
                            </Menu.Item>
                            <Menu.Item leftSection={<KeyRound size={14} />} onClick={() => void resendEmail(vm.resendInvite(u.id, 'reset'), 'reset')}>
                                Send password reset
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item onClick={() => void act(vm.setUserEnabled(u.id, !enabled), enabled ? 'User disabled' : 'User enabled')}>
                                {enabled ? 'Disable' : 'Enable'}
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item color="red" onClick={() => void act(vm.deleteUser(u.id), 'User deleted')}>Delete</Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                );
            },
        },
    ];

    return (
        <Stack gap="md">
            <Group justify="space-between" align="flex-end">
                <Box>
                    <Text size="sm" fw={600}>Users</Text>
                    <Text size="xs" c="dimmed">Members of this tenant&apos;s identity realm.</Text>
                </Box>
                <Button size="xs" leftSection={<Plus size={15} />} onClick={() => setInviteOpen(true)}>Invite user</Button>
            </Group>
            <MegaGrid
                persistenceKey="operator-tenant-users"
                columns={columns}
                rows={vm.users ?? []}
                getRowKey={(u) => u.id}
                searchAccessor={(u) => `${u.email} ${u.displayName}`}
                searchPlaceholder="Search users"
                emptyMessage="No users"
                loading={vm.loading}
                clientPageSize={25}
            />
            <InviteUserModal opened={inviteOpen} onClose={() => setInviteOpen(false)} vm={vm} />
        </Stack>
    );
}

function InviteUserModal({ opened, onClose, vm }: { opened: boolean; onClose: () => void; vm: OperatorTenantDetailViewModel }): React.ReactElement {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [roles, setRoles] = useState<string[]>([Roles.VIEWER]);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (): Promise<void> => {
        setSubmitting(true);
        try {
            await vm.inviteUser({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), roles });
            notifications.show({ color: 'green', message: `Invited ${email}` });
            setEmail(''); setFirstName(''); setLastName(''); setRoles([Roles.VIEWER]);
            onClose();
        } catch (e) {
            notifications.show({ color: 'red', title: 'Invite failed', message: e instanceof Error ? e.message : 'Unexpected error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Invite user" centered>
            <Stack gap="sm">
                <TextInput label="Email" placeholder="user@tenant.com" value={email} onChange={(e) => setEmail(e.currentTarget.value)} required />
                <Group grow>
                    <TextInput label="First name" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} />
                    <TextInput label="Last name" value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} />
                </Group>
                <MultiSelect label="Roles" data={TENANT_ROLE_OPTIONS} value={roles} onChange={setRoles} />
                <Group justify="flex-end" mt="sm">
                    <Button variant="subtle" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => void submit()} loading={submitting} disabled={!email.trim()}>Invite</Button>
                </Group>
            </Stack>
        </Modal>
    );
}

function ManagementTab({ vm }: { vm: OperatorTenantDetailViewModel }): React.ReactElement {
    const [planId, setPlanId] = useState<string | null>(vm.plans.find((p) => p.name === vm.entitlement?.planName)?.id ?? null);
    const [status, setStatus] = useState(vm.entitlement?.status ?? 'active');
    const [features, setFeatures] = useState<string[]>(vm.entitlement?.enabledFeatures ?? []);
    const [seatCap, setSeatCap] = useState<number | ''>(vm.entitlement?.seatCap ?? '');
    const [offboardOpen, setOffboardOpen] = useState(false);

    const t = vm.tenant;
    const canSuspend = t?.status === 'active' || t?.status === 'trial';
    const canResume = t?.status === 'suspended';
    const canOffboard = t != null && t.status !== 'cancelled';

    const saveEntitlement = (): Promise<void> => act(
        vm.saveEntitlement({ planName: vm.entitlement?.planName ?? planId ?? 'custom', status, enabledFeatures: features, ...(seatCap === '' ? {} : { seatCap: Number(seatCap) }) }),
        'Entitlement saved',
    );

    return (
        <Stack gap="lg">
            <ManageCard title="Plan" description="Assign a catalog plan — replaces the entitlement's features and quotas.">
                <Group align="flex-end" gap="sm" maw={560}>
                    <Select
                        placeholder="Select a plan" style={{ flex: 1 }}
                        data={vm.plans.map((p) => ({ value: p.id, label: `${p.displayName} (${p.priceMonthly === null ? 'custom' : `$${p.priceMonthly}`})` }))}
                        value={planId} onChange={setPlanId} searchable
                    />
                    <Button loading={vm.busy} disabled={!planId} onClick={() => planId && void act(vm.assignPlan(planId), 'Plan assigned')}>Assign plan</Button>
                </Group>
            </ManageCard>

            <ManageCard title="Entitlement override" description="Fine-tune this tenant's status, features, and seat cap directly.">
                <Stack gap="md">
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" verticalSpacing="md">
                        <Select label="Status" data={ENTITLEMENT_STATUS_OPTIONS} value={status} onChange={(v) => setStatus(v ?? 'active')} />
                        <NumberInput label="Seat cap" value={seatCap} onChange={(v) => setSeatCap(typeof v === 'number' ? v : '')} min={0} allowDecimal={false} />
                    </SimpleGrid>
                    <MultiSelect
                        label="Enabled features"
                        data={[
                            { group: 'Data & analytics (drives classification)', items: FEATURE_CATALOG.filter((f) => f.requiresIngestion).map((f) => ({ value: f.key, label: f.label })) },
                            { group: 'Platform', items: FEATURE_CATALOG.filter((f) => !f.requiresIngestion).map((f) => ({ value: f.key, label: f.label })) },
                        ]}
                        value={features} onChange={setFeatures} searchable
                    />
                    <Group><Button variant="light" loading={vm.busy} onClick={() => void saveEntitlement()}>Save entitlement</Button></Group>
                </Stack>
            </ManageCard>

            <Box style={{ ...CARD_STYLE, padding: 'var(--mantine-spacing-lg)', borderColor: 'var(--bm-color-danger-border)' }}>
                <Text size="sm" fw={600} style={{ color: 'var(--bm-color-danger-text)' }}>Danger zone</Text>
                <Text size="xs" c="dimmed" mb="md">Suspending blocks tenant access. Offboarding is permanent and triggers a cross-service data purge.</Text>
                <Group justify="space-between" gap="sm" wrap="wrap">
                    <Group gap="sm">
                        {canSuspend && <Button variant="default" loading={vm.busy} onClick={() => void act(vm.suspend(), 'Tenant suspended')}>Suspend</Button>}
                        {canResume && <Button variant="default" loading={vm.busy} onClick={() => void act(vm.resume(), 'Tenant resumed')}>Resume</Button>}
                    </Group>
                    {canOffboard && <Button color="red" onClick={() => setOffboardOpen(true)}>Offboard…</Button>}
                </Group>
            </Box>

            <OffboardModal opened={offboardOpen} onClose={() => setOffboardOpen(false)} vm={vm} />
        </Stack>
    );
}

function OffboardModal({ opened, onClose, vm }: { opened: boolean; onClose: () => void; vm: OperatorTenantDetailViewModel }): React.ReactElement {
    const [confirm, setConfirm] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const id = vm.tenant?.id ?? '';

    const submit = async (): Promise<void> => {
        setSubmitting(true);
        try {
            await vm.offboard(reason.trim() || undefined);
            notifications.show({ color: 'green', message: `Offboarded ${id}` });
            setConfirm(''); setReason('');
            onClose();
        } catch (e) {
            notifications.show({ color: 'red', title: 'Offboard failed', message: e instanceof Error ? e.message : 'Unexpected error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Offboard tenant" centered>
            <Stack gap="sm">
                <Alert color="red" icon={<AlertTriangle size={16} />}>
                    This cancels the tenant, deletes its identity realm, and triggers a cross-service data purge. This cannot be undone.
                </Alert>
                <Text size="sm">Type <b>{id}</b> to confirm.</Text>
                <TextInput value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} placeholder={id} />
                <Textarea label="Reason (optional)" value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} />
                <Group justify="flex-end" mt="sm">
                    <Button variant="subtle" onClick={onClose}>Cancel</Button>
                    <Button color="red" onClick={() => void submit()} loading={submitting} disabled={confirm !== id}>Offboard</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
